/**
 * MANAGEMENT (14) / ONBOARDING — the WRITE hooks for the lab-provisioning screen. ADDITIVE to the shell
 * `../api.ts`, which owns the reads (`useLabs`/`useLab` + the `managementKeys` factory + `isManagementAbsent`).
 * The screen imports `useLabs` from the shell `../api` and the two mutations below from here.
 *
 * THE WRITE CONVENTION (the exact pattern the shell `../api.ts` documents): a mutation is
 * `useApiMutation((vars) => api.<verb>("/management/...", body), { invalidate: [...] })`, and it
 * invalidates `managementKeys.root` so the WHOLE cross-lab estate refreshes (the directory list, the
 * Overview KPIs, and any open drill-down all re-read after a provision/lifecycle write). The platform
 * keys are rooted under `["platform","management",…]`, NOT `qk.scoped`, so a lab switch never touches them.
 *
 * Endpoints (code-verified against server/src/modules/management/onboarding.routes.js):
 *   POST /management/labs              management.lab.onboard   → provision a new lab + first owner
 *   POST /management/labs/:id/statut   management.lab.onboard   → drive lifecycle (actif ⇄ suspendu)
 */
import { api, managementKeys, useApiMutation } from "../api";
import type {
  ProvisionLabResult,
  ProvisionLabVars,
  SetLabStatutResult,
  SetLabStatutVars,
} from "./onboarding.types";

/**
 * POST /management/labs — provision a NEW lab + its first owner (atomic, server-side). On success the
 * server returns the new lab row + the owner identity; we invalidate the whole management root so the
 * directory + the Overview KPIs re-read. A reference collision / duplicate owner email surfaces as a typed
 * 409 ApiError the form maps to an inline message.
 */
export function useProvisionLab() {
  return useApiMutation<ProvisionLabResult, ProvisionLabVars>(
    (vars) => api.post<ProvisionLabResult>("/management/labs", vars),
    { invalidate: [managementKeys.root] },
  );
}

/**
 * POST /management/labs/:id/statut — drive the lab lifecycle (actif ⇄ suspendu), correct-by-reissue and
 * version-guarded (`expected_version` → optimistic concurrency; a stale version is a typed 409). The id
 * rides the path; the rest is the body. Invalidates the management root so the row's badge + the KPIs flip.
 */
export function useSetLabStatut() {
  return useApiMutation<SetLabStatutResult, SetLabStatutVars>(
    ({ id, ...body }) => api.post<SetLabStatutResult>(`/management/labs/${id}/statut`, body),
    { invalidate: [managementKeys.root] },
  );
}
